<template>
  <el-dialog
    title="过磅出厂传输SAP确认"
    :visible.sync="visible"
    width="640px"
    @close="onClose"
  >
    <span>确认传输SAP吗？</span>
    <span style="color: red;" v-if="diffExcess"
      >(同车牌比上次空车差300KG（相差{{ diffValue }}KG）以上需确认)</span
    >
    <div class="py-2" v-if="isHuafu">
      当前装卸信息：存储地点 {{ detail.storage_point_name || "--" }}；
    </div>
    <div class="py-2" v-else-if="detail.booking_queue">
      当前装卸信息：存储地点 {{ detail.booking_queue.storage_point_id }}（{{
        detail.booking_queue.storage_point_name
      }}）、垛位 {{ detail.booking_queue.stack_position_id }}（{{
        detail.booking_queue.stack_position_name
      }}）、卸货班组 {{ detail.booking_queue.unload_team_name }}；
    </div>
    <el-form v-if="!isHuafu" ref="form" :model="detail" label-width="120px" :rules="rules" class="pb-2">
      <el-form-item label="存储地点" prop="storage_point_id">
        <el-select
          class="w-full"
          filterable
          v-model="detail.storage_point_id"
          placeholder="请选择存储地点"
          @change="onStoragePointChange"
        >
          <el-option
            v-for="item in storagePointList"
            :key="item.storage_point_id"
            :label="getStoragePointOptionLabel(item)"
            :value="item.storage_point_id"
          ></el-option>
        </el-select>
      </el-form-item>
    </el-form>
    <el-input
      :autosize="{ minRows: 4, maxRows: 6 }"
      class="w-full"
      type="textarea"
      v-model="detail.remark"
      placeholder="请输入备注说明"
    ></el-input>
    <span slot="footer" class="dialog-footer">
      <el-button size="small" @click="visible = false">取 消</el-button>
      <el-button size="small" type="primary" @click="onOK">确认传输</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { cloneDeep, debounce } from "lodash";
export default {
  data() {
    return {
      visible: false,
      rules: {},
      detail: {
        booking_queue: {}
      },
      storagePointList: []
    };
  },
  computed: {
    isHuafu() {
      return this.detail.order_type === "NB";
    },
    diffExcess() {
      let result = false;
      const { last_booking_pound, weight } = this.detail;
      if (last_booking_pound) {
        result =
          Math.abs(Number(weight) - Number(last_booking_pound.weight)) > 0.3;
      }
      return result;
    },
    diffValue() {
      let diff_weight = 0;
      try {
        const { last_booking_pound, weight } = this.detail;
        if (last_booking_pound) {
          diff_weight = Math.abs(
            Number(weight) - Number(last_booking_pound.weight)
          );
        }
      } catch (error) {
        console.log(error);
      }
      return (diff_weight * 1000).toFixed(0);
    }
  },
  methods: {
    // 打开弹窗时先补查最新过磅详情，避免列表行未带全订单扩展字段时出现回显为空。
    async onOpen(data) {
      const hydratedDetail = await this.hydrateDetailWithLatestData(data);
      const queueDetail = hydratedDetail.booking_queue || {};
      const defaultStoragePoint = this.getDefaultStoragePoint(
        hydratedDetail,
        queueDetail
      );
      this.detail = cloneDeep({
        ...hydratedDetail,
        car_business_id:
          queueDetail.car_business_id || hydratedDetail.car_business_id || "",
        storage_point_id: defaultStoragePoint.storage_point_id,
        storage_point_name: defaultStoragePoint.storage_point_name,
        warehouse_id: queueDetail.warehouse_id || hydratedDetail.warehouse_id || "",
        warehouse_name:
          queueDetail.warehouse_name || hydratedDetail.warehouse_name || "",
        package_count:
          hydratedDetail.package_count === null || hydratedDetail.package_count === undefined
            ? ""
            : String(hydratedDetail.package_count),
        // 国废“小包”字段统一沿用 SAP 的空串 / X 口径，避免旧数值型映射把“是”回显成“否”。
        is_small_package: hydratedDetail.is_small_package === "X" ? "X" : "",
        carrier_type:
          hydratedDetail.carrier_type === null ||
          hydratedDetail.carrier_type === undefined ||
          hydratedDetail.carrier_type === ""
            ? ""
            : String(hydratedDetail.carrier_type),
        booking_queue: queueDetail || (data && data.booking_queue) || {}
      });
      this.storagePointList = [];
      this.visible = true;
      // 关键步骤：化辅出厂只读展示预约存储地点，不再请求可选点位。
      if (this.shouldLoadStoragePoint()) {
        await this.getStoragePoint();
      }
    },
    // 判断出厂弹窗是否需要加载存储地点选项。
    shouldLoadStoragePoint() {
      return !this.isHuafu;
    },
    // 统一把存储地点编码转成字符串，避免 el-select 因数字/字符串类型不一致只显示原始值。
    normalizeStoragePointId(value) {
      return value === null || value === undefined || value === ""
        ? ""
        : String(value);
    },
    // 化辅优先沿用预约卸货点，普通业务继续使用原有存储地点事实。
    getDefaultStoragePoint(detail, queueDetail) {
      if (detail.order_type === "NB") {
        return this.buildHuafuStoragePointOption({
          storage_point_id:
            queueDetail.storage_point_id || detail.storage_point_id || "",
          storage_point_name:
            queueDetail.storage_point_name || detail.storage_point_name || "",
          warehouse_id: queueDetail.warehouse_id || detail.warehouse_id || "",
          warehouse_name:
            queueDetail.warehouse_name || detail.warehouse_name || ""
        });
      }
      return {
        storage_point_id: this.normalizeStoragePointId(
          queueDetail.storage_point_id || detail.storage_point_id || ""
        ),
        storage_point_name:
          queueDetail.storage_point_name || detail.storage_point_name || ""
      };
    },
    // 化辅装卸点按 SAP“编码-名称”维护，页面使用名称展示、编码提交。
    buildHuafuStoragePointOption(option = {}) {
      const storagePointName =
        option.storage_point_name ||
        option.storage_point_label ||
        option.warehouse_name ||
        "";
      const storagePointId = this.extractHuafuStoragePointCode(
        storagePointName,
        option.storage_point_id || option.warehouse_id || ""
      );
      return {
        ...option,
        storage_point_id: this.normalizeStoragePointId(storagePointId),
        storage_point_name: storagePointName,
        storage_point_label: storagePointName
      };
    },
    // 从“编码-名称”中拆出四位 SAP 存储地点编码，内部装卸点 UUID 不能作为旧值兜底。
    extractHuafuStoragePointCode(displayValue, fallbackValue = "") {
      const normalizedDisplayValue = String(displayValue || "").trim();
      const matched = normalizedDisplayValue.match(
        /^([A-Za-z0-9]{4})\s*-\s*.+$/u
      );
      if (matched && matched[1]) {
        return matched[1].trim();
      }
      const normalizedFallbackValue = String(fallbackValue || "").trim();
      return /^[A-Za-z0-9]{4}$/.test(normalizedFallbackValue)
        ? normalizedFallbackValue
        : "";
    },
    // 统一整理存储地点选项，保证 option value 与弹窗当前值使用同一类型。
    normalizeStoragePointList(list = []) {
      return list.map(item => ({
        ...item,
        storage_point_id: this.normalizeStoragePointId(item.storage_point_id)
      }));
    },
    // 化辅只展示装卸点“编码-名称”，普通业务保留原编码加名称格式。
    getStoragePointOptionLabel(item) {
      if (this.isHuafu) {
        return item.storage_point_label || item.storage_point_name || "";
      }
      return `${item.storage_point_id}（${item.storage_point_name}）`;
    },
    // 关键步骤：列表页“传输SAP”直接透传当前行数据，这里补查详情以带回预约主表上的国废字段。
    async hydrateDetailWithLatestData(data) {
      const poundId = data && data.id ? data.id : "";
      if (!poundId) {
        return data || {};
      }
      try {
        const dataObj = await this.$common.http({
          url: "/api/queue/admin/detail_booking_pound",
          data: {
            id: poundId
          },
          noLoading: true
        });
        const latestDetail = dataObj.data.data || {};
        return {
          ...data,
          ...latestDetail,
          booking_queue: latestDetail.booking_queue || data.booking_queue || {}
        };
      } catch (error) {
        return data || {};
      }
    },
    onStoragePointChange(value) {
      const storagePoint = this.storagePointList.find(
        item => item.storage_point_id === this.normalizeStoragePointId(value)
      );
      this.detail.storage_point_name = storagePoint
        ? storagePoint.storage_point_name
        : "";
    },
    // 下拉数据加载完成后对齐当前选中项，确保回显显示“编码（名称）”而不是裸编码。
    syncStoragePointSelection() {
      const normalizedStoragePointId = this.normalizeStoragePointId(
        this.detail.storage_point_id
      );
      this.detail.storage_point_id = normalizedStoragePointId;
      const matchedStoragePoint = this.storagePointList.find(
        item => item.storage_point_id === normalizedStoragePointId
      );
      if (matchedStoragePoint) {
        this.detail.storage_point_id = matchedStoragePoint.storage_point_id;
        this.detail.storage_point_name = matchedStoragePoint.storage_point_name;
        return;
      }
      if (this.isHuafu) {
        const matchedWarehousePoint = this.storagePointList.find(
          item => item.warehouse_id === this.detail.warehouse_id
        );
        if (matchedWarehousePoint) {
          this.detail.storage_point_id = matchedWarehousePoint.storage_point_id;
          this.detail.storage_point_name = matchedWarehousePoint.storage_point_name;
        }
      }
    },
    onClose() {
      this.detail.remark = null;
      this.storagePointList = [];
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
    // 查询出厂存储地点；化辅按车辆业务关联装卸点查询，其它业务保持原接口。
    async getStoragePoint() {
      try {
        if (this.isHuafu && !this.detail.car_business_id) {
          this.storagePointList = this.appendBookedHuafuStoragePointOption([]);
          this.syncStoragePointSelection();
          return;
        }
        const requestConfig = this.isHuafu
          ? {
            url: "/api/queue/dispatcher/query_warehouse_by_business_id",
            data: {
              car_business_id: this.detail.car_business_id
            }
          }
          : {
            url: "/api/queue/dispatcher/query_storage_point"
          };
        const dataObj = await this.$common.http(requestConfig);
        const storagePointList = dataObj.data.data || [];
        this.storagePointList = this.isHuafu
          ? this.appendBookedHuafuStoragePointOption(
            storagePointList.map(item =>
              this.buildHuafuStoragePointOption(item)
            )
          )
          : this.normalizeStoragePointList(storagePointList);
        this.syncStoragePointSelection();
      } catch (error) {
        if (this.isHuafu) {
          this.storagePointList = this.appendBookedHuafuStoragePointOption([]);
          this.syncStoragePointSelection();
        }
      }
    },
    // 当前预约点不在最新配置中时也保留回显，避免历史预约只能看到裸 UUID。
    appendBookedHuafuStoragePointOption(storagePointList) {
      if (!this.isHuafu) {
        return storagePointList;
      }
      const bookedStoragePoint = this.buildHuafuStoragePointOption({
        storage_point_id: this.detail.storage_point_id || "",
        storage_point_name: this.detail.storage_point_name || "",
        warehouse_id: this.detail.warehouse_id || "",
        warehouse_name: this.detail.warehouse_name || ""
      });
      if (
        !bookedStoragePoint.storage_point_id ||
        !bookedStoragePoint.storage_point_name
      ) {
        return storagePointList;
      }
      const exists = storagePointList.some(
        item =>
          item.storage_point_id === bookedStoragePoint.storage_point_id
      );
      return exists
        ? storagePointList
        : [bookedStoragePoint, ...storagePointList];
    },
    // 组装出厂传 SAP 参数，化辅不允许通过页面覆盖存储地点。
    buildSubmitData(detail) {
      const submitData = {
        id: detail.id,
        put_area_name: detail.put_area_name,
        remark: detail.remark,
        order_item: detail.order_item,
        package_count: detail.package_count,
        is_small_package: detail.is_small_package,
        carrier_type: detail.carrier_type
      };
      if (!this.isHuafu) {
        submitData.storage_point_id = detail.storage_point_id;
        submitData.storage_point_name = detail.storage_point_name;
      }
      return submitData;
    },
    // 执行出厂提交校验，化辅隐藏编辑表单后直接通过。
    validateBeforeSubmit(callback) {
      if (this.isHuafu) {
        callback(true);
        return;
      }
      this.$refs.form.validate(callback);
    },
    onOK() {
      const handler = debounce(async () => {
        this.$common.pcToastLoading("正在传输SAP，请稍等...");
        try {
          const result = await this.$common.http({
            url: "/api/queue/admin/push_sap",
            data: this.buildSubmitData(this.detail)
          });
          if (result.data.data.flag == 1) {
            this.$message.success("SAP数据已发送");
          } else {
            this.$message.error(result.data.data.message);
          }
          this.$emit("ok");
          this.visible = false;
          setTimeout(() => {
            this.$common.pcToastClose();
          }, 1500);
        } catch (error) {
          console.log(error);
          this.$common.pcToastClose();
        }
      }, 300);
      this.validateBeforeSubmit(valid => {
        if (!valid) {
          return;
        }
        if (this.diffExcess) {
          this.$confirm("数据异常，还是继续传输SAP吗？", "提示").then(() => {
            handler();
          });
        } else {
          handler();
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
/deep/ .el-dialog__body {
  padding: 10px 15px 0px !important;
}
</style>
